/**
 * Helpers for building and maintaining per-curve timezone mappings in plot preferences.
 *
 * @packageDocumentation
 * @module CurveTimezoneMapping
 * @public
 */

import { record as R } from '@highimpact/data-utils';
import { CurveId } from './plotTypes';

// -- type definitions --

/** Minimal source shape required to derive a curve timezone. */
export interface TimezoneSource {
  readonly timezone?: string | null;
}

/** Init settings fragment used when seeding {@link PlotPreferencesBase.curveToTimezone}. */
export interface CurveTimezoneInitSettings {
  readonly curveId: CurveId;
  readonly timezone?: string;
}

// -- constants --

/** Fallback IANA timezone when a source does not specify one. */
export const DEFAULT_CURVE_TIMEZONE = 'UTC';

// -- helper functions --

/**
 * Resolves the IANA timezone for a single source.
 *
 * @param source - A measure source that may carry an optional timezone.
 * @returns The source timezone or {@link DEFAULT_CURVE_TIMEZONE}.
 */
export function timezoneFromSource(source: TimezoneSource): string {
  return source.timezone ?? DEFAULT_CURVE_TIMEZONE;
}

/**
 * Builds a curve-to-timezone map from init settings.
 *
 * @param settings - Curve init settings produced from selected sources.
 * @returns A record mapping each curve id to an IANA timezone.
 */
export function buildCurveToTimezoneFromInitSettings(
  settings: readonly CurveTimezoneInitSettings[]
): Record<CurveId, string> {
  return R.fromArray(
    settings.map((s) => [s.curveId, s.timezone ?? DEFAULT_CURVE_TIMEZONE]),
  );
}

/**
 * Returns a copy of `curveToTimezone` with one curve entry set.
 *
 * @param curveToTimezone - Existing curve timezone map.
 * @param curveId - Target curve id.
 * @param timezone - IANA timezone to assign.
 * @returns A new map including the updated entry.
 */
export function upsertCurveTimezone(
  curveToTimezone: Readonly<Record<CurveId, string>>,
  curveId: CurveId,
  timezone: string
): Record<CurveId, string> {
  return {
    ...curveToTimezone,
    [curveId]: timezone,
  };
}

/**
 * Returns a copy of `curveToTimezone` with one curve entry removed.
 *
 * @param curveToTimezone - Existing curve timezone map.
 * @param curveId - Curve id to remove.
 * @returns A new map without the given curve id.
 */
export function removeCurveTimezone(
  curveToTimezone: Readonly<Record<CurveId, string>>,
  curveId: CurveId
): Record<CurveId, string> {
  return R.filterWithKeys((cId) => cId !== curveId, curveToTimezone);
}

/**
 * Assigns a default timezone to a newly added curve when no explicit value is provided.
 *
 * @param curveToTimezone - Existing curve timezone map.
 * @param curveId - Newly added curve id.
 * @returns A new map containing the default timezone for the new curve.
 */
export function assignDefaultTimezoneForNewCurve(
  curveToTimezone: Readonly<Record<CurveId, string>>,
  curveId: CurveId
): Record<CurveId, string> {
  return {
    ...curveToTimezone, 
    [curveId]: DEFAULT_CURVE_TIMEZONE,
  };
}

/**
 * Normalizes a persisted or partial curve timezone map during preference upgrade.
 *
 * @param curveToTimezone - Persisted curve timezone map, if any.
 * @returns A normalized map safe to attach to upgraded preferences.
 */
export function upgradeCurveToTimezone(
  curveToTimezone?: Readonly<Record<CurveId, string>>
): Record<CurveId, string> {
  return curveToTimezone 
    ? R.map((timezone) => timezone ?? DEFAULT_CURVE_TIMEZONE, curveToTimezone) 
    : {};
}
